<template>
  <!-- 首頁 04 實驗室（2026-10-04 首頁進化；巡檢報告 30 號建議的「實驗室帶」）：
       左邊一件作品（桌機捲到這裡、頁面閒下來才在原地跑起來；手機與減少動態只放海報，按了才跑），右邊他的一句話、四個數字、一首作品的詩。
       資料全部是 /lab/ 頁同一份快照（src/data/lab-home.json），只取用得到的欄位（Vite 對 JSON 的具名匯入會把 family、recent 搖掉）。 -->
  <section id="home-lab" ref="root" class="home-sec home-lab" aria-labelledby="home-lab-title">
    <div class="home-head">
      <p class="home-eyebrow">04 · LAB · {{ stats.yearFrom }}–{{ stats.yearTo }}</p>
      <h2 id="home-lab-title">{{ t.title }}</h2>
      <p class="home-note">{{ t.note(stats.total) }}</p>
    </div>
    <div class="home-lab-grid">
      <figure class="home-lab-specimen">
        <div ref="frameEl" class="home-lab-frame" :class="{ 'is-live': frameLoaded }">
          <img loading="lazy" decoding="async" :width="piece.w" :height="piece.h" :src="near ? `/static/lab-home/${piece.id}-poster-960.webp` : null" :alt="t.posterAlt(name(piece))" />
          <iframe
            v-if="running" :key="piece.id" class="home-lab-live"
            :src="piece.run + '?kiosk=1'" :title="t.frameTitle(name(piece))"
            :width="piece.w" :height="piece.h"
            :style="{ width: piece.w + 'px', height: piece.h + 'px', transform: `scale(${scale})` }"
            sandbox="allow-scripts allow-same-origin allow-pointer-lock" allow="accelerometer; gyroscope" referrerpolicy="strict-origin-when-cross-origin" tabindex="-1"
            @load="onFrameLoad"
          ></iframe>
        </div>
        <figcaption>
          <a :href="piece.url" rel="noopener"><b>{{ name(piece) }}</b> <time :datetime="piece.date">{{ piece.date }}</time></a>
          <span class="home-lab-ctrl">
            <button v-if="running" type="button" @click="stop"><BrandIcon name="stop" />{{ t.stop }}</button>
            <button v-else type="button" @click="start(true)"><BrandIcon name="run" />{{ t.run }}</button>
          </span>
        </figcaption>
      </figure>
      <div class="home-lab-side">
        <blockquote class="quote home-lab-motto">
          <p>{{ t.motto }}</p>
          <cite>{{ t.mottoCite }}</cite>
        </blockquote>
        <dl class="home-lab-stats">
          <div><dt>{{ t.sTotal }}</dt><dd>{{ stats.total }}</dd></div>
          <div><dt>{{ t.sFeatured }}</dt><dd>{{ stats.featured }}</dd></div>
          <div><dt>{{ t.sPoems }}</dt><dd>{{ stats.poems }}</dd></div>
          <div><dt>{{ t.sFamilies }}</dt><dd>{{ stats.families }}</dd></div>
        </dl>
        <figure class="home-lab-poem" :class="{ swapping }">
          <p v-for="(l, i) in poem.lines" :key="poem.id + i">{{ l }}</p>
          <figcaption>
            <a :href="poem.url" rel="noopener">{{ t.poemOf(name(poem)) }}・{{ poem.date }}</a>
            <button type="button" @click="nextPoem">{{ t.nextPoem }}</button>
          </figcaption>
        </figure>
        <p class="home-actions">
          <RouterLink class="home-pill is-primary" :to="local('/lab')">{{ t.enter }} →</RouterLink>
          <a class="home-pill" href="https://lab.cheyuwu.com/" rel="noopener">lab.cheyuwu.com ↗︎</a>
        </p>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { stats, hero as heroes, poems, fetchedOn } from '@/data/lab-home.json'
import { sitePath } from '@/runtime/site-path.mjs'
import { isBot, prefersReducedMotion } from '@/runtime/view-transition'
import BrandIcon from '@/components/BrandIcon.vue'

const props = defineProps({ zh: Boolean })
const local = (p) => sitePath(p, props.zh)
const name = (x) => (props.zh ? x.titleZh || x.title : x.title)
// 同一次建置，預渲染與瀏覽器挑到同一件、同一首；跟 /lab/ 頁首錯開（加鹽），兩頁不會放同一件
const hash = (s) => { let h = 2166136261; for (const c of String(s)) { h ^= c.codePointAt(0); h = Math.imul(h, 16777619) >>> 0 } return h }
const pool = heroes.filter((h) => h.mobile).length ? heroes.filter((h) => h.mobile) : heroes
const piece = computed(() => pool[hash(fetchedOn + 'home') % pool.length])
const poemIdx = ref(hash(fetchedOn + 'home-poem') % poems.length)
const poem = computed(() => poems[poemIdx.value])
const swapping = ref(false)
function nextPoem () {
  const swap = () => { let i = poemIdx.value; while (poems.length > 1 && i === poemIdx.value) i = Math.floor(Math.random() * poems.length); poemIdx.value = i }
  if (prefersReducedMotion()) return swap()
  swapping.value = true
  setTimeout(() => { swap(); swapping.value = false }, 180)
}

// ── 在原地跑：桌機（寬螢幕、精確指標）捲進畫面、load＋閒置之後才載 iframe；捲出 1.2 秒或分頁到背景就卸載 ──
// 手機、減少動態、省流量、爬蟲不自動跑（p5 在慢手機上是一秒多的長任務，見 DESIGN §二十），按「執行」才跑。
const root = ref(null), frameEl = ref(null)
const running = ref(false), frameLoaded = ref(false), scale = ref(0.5), near = ref(false)
let io = null, nio = null, ro = null, settle = 0, leave = 0, armed = false, inView = false, userStopped = false, auto = false
const fit = () => { if (frameEl.value) scale.value = frameEl.value.clientWidth / piece.value.w }
function start (byUser = false) { if (byUser) userStopped = false; if (running.value) return; fit(); frameLoaded.value = false; running.value = true }
function unload () { running.value = false; frameLoaded.value = false; clearTimeout(settle) }
function stop () { userStopped = true; unload() }
function onFrameLoad () { clearTimeout(settle); settle = setTimeout(() => { frameLoaded.value = true }, 700) }
const maybeRun = () => { if (auto && armed && inView && !userStopped && !document.hidden) start() }
function onVisibility () { if (document.hidden) unload(); else maybeRun() }

onMounted(() => {
  if (typeof ResizeObserver === 'function' && frameEl.value) { ro = new ResizeObserver(fit); ro.observe(frameEl.value) }
  // 海報：捲到前 600px 才給 src；預渲染的靜態 HTML 不給（手機瀏覽器會在 JS 接手前照樣下載靜態 HTML 裡的 lazy 圖）
  if (!(isBot() && navigator.webdriver === true)) {
    if (typeof IntersectionObserver === 'function' && root.value) { nio = new IntersectionObserver((es) => { if (es.some((e) => e.isIntersecting)) { near.value = true; nio.disconnect(); nio = null } }, { rootMargin: '600px 0px' }); nio.observe(root.value) } else near.value = true
  }
  if (isBot()) return
  const c = typeof navigator !== 'undefined' && navigator.connection
  const desktop = matchMedia('(min-width: 1000px) and (pointer: fine)').matches
  auto = desktop && !prefersReducedMotion() && !(c && c.saveData) && (navigator.hardwareConcurrency || 8) > 4
  if (typeof IntersectionObserver === 'function' && frameEl.value) {
    io = new IntersectionObserver((entries) => {
      inView = entries[entries.length - 1].isIntersecting
      clearTimeout(leave)
      if (inView) maybeRun()
      else leave = setTimeout(() => { if (!inView) unload() }, 1200)
    }, { threshold: 0.25 })
    io.observe(frameEl.value)
  }
  document.addEventListener('visibilitychange', onVisibility)
  const arm = () => { armed = true; maybeRun() }
  const idle = () => ('requestIdleCallback' in window ? requestIdleCallback(arm, { timeout: 2500 }) : setTimeout(arm, 1200))
  if (document.readyState === 'complete') idle()
  else window.addEventListener('load', idle, { once: true })
})
onBeforeUnmount(() => { io?.disconnect(); nio?.disconnect(); ro?.disconnect(); clearTimeout(settle); clearTimeout(leave); document.removeEventListener('visibilitychange', onVisibility) })

const ZH = {
  title: '實驗室',
  note: (n) => `2018 年起的每日創作，${n} 件，每一件都可以在頁面裡跑。`,
  motto: '我想要每個作品都是一個數位生命體，這樣更好玩一點。',
  mottoCite: '吳哲宇，2026 年 10 月',
  sTotal: '件作品', sFeatured: '件精選', sPoems: '首詩', sFamilies: '個家族',
  posterAlt: (n) => `〈${n}〉跑起來的畫面`,
  frameTitle: (n) => `〈${n}〉，在頁面裡執行`,
  run: '執行', stop: '停止',
  poemOf: (n) => `〈${n}〉的詩`,
  nextPoem: '換一首',
  enter: '進入實驗室'
}
const EN = {
  title: 'Lab',
  note: (n) => `Daily coding since 2018: ${n} pieces, each one runs right on the page.`,
  motto: 'I want every work to be a digital life form. It is more fun that way.',
  mottoCite: 'Che-Yu Wu, October 2026 (translated from Chinese)',
  sTotal: 'works', sFeatured: 'featured', sPoems: 'poems', sFamilies: 'families',
  posterAlt: (n) => `${n}, a frame from the running piece`,
  frameTitle: (n) => `${n}, running on the page`,
  run: 'Run', stop: 'Stop',
  poemOf: (n) => `Poem from ${n}`,
  nextPoem: 'Another poem',
  enter: 'Enter the Lab'
}
const t = computed(() => (props.zh ? ZH : EN))
</script>

<style>
.home-lab-grid { display: grid; grid-template-columns: minmax(0, 7fr) minmax(0, 5fr); gap: clamp(28px, 4vw, 64px); align-items: start; }
.home-lab-frame { position: relative; aspect-ratio: 1 / 1; max-height: 78vh; margin: 0 auto; overflow: hidden; background: var(--bg1); }
.home-lab-frame img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: contain; display: block; transition: opacity .6s ease; }
.home-lab-frame.is-live img { opacity: 0; }
.home-lab-live { position: absolute; top: 0; left: 0; border: 0; transform-origin: 0 0; }
.home-lab-specimen { margin: 0; }
.home-lab-specimen figcaption { display: flex; justify-content: space-between; align-items: baseline; gap: 16px; padding-top: 12px; font-size: .86rem; color: var(--ink-2); }
.home-lab-specimen figcaption a { color: var(--ink); text-decoration: none; }
.home-lab-specimen figcaption a:hover b { color: var(--gold); }
.home-lab-specimen time { color: var(--ink-3); margin-left: 8px; font-variant-numeric: tabular-nums; }
.home-lab-ctrl button, .home-lab-poem button { display: inline-flex; align-items: center; gap: 6px; background: var(--tint-2); color: var(--ink); border: 0; border-radius: var(--r-pill); padding: 7px 14px; font-size: .8rem; cursor: pointer; }
.home-lab-ctrl button:hover, .home-lab-poem button:hover { background: var(--tint-3); }
.home-lab-ctrl svg { width: 14px; height: 14px; }
.home-lab-motto { margin: 0 0 28px; }
.home-lab-stats { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin: 0 0 28px; }
.home-lab-stats div { display: flex; flex-direction: column-reverse; }
.home-lab-stats dt { font-size: .74rem; color: var(--ink-3); font-weight: 400; margin: 0; }
.home-lab-stats dd { font-family: var(--font-slab); font-size: clamp(1.8rem, 2.6vw, 2.6rem); line-height: 1.1; margin: 0; font-variant-numeric: tabular-nums; }
.home-lab-poem { margin: 0 0 28px; padding: 22px 24px; background: var(--bg1); border-radius: var(--r-lg); transition: opacity .18s ease; }
.home-lab-poem.swapping { opacity: 0; }
.home-lab-poem > p { font-family: var(--font-display); font-size: clamp(1rem, 1.3vw, 1.18rem); line-height: 1.9; margin: 0; }
.home-lab-poem figcaption { display: flex; justify-content: space-between; align-items: center; gap: 12px; margin-top: 14px; font-size: .78rem; color: var(--ink-3); }
.home-lab-poem figcaption a { color: inherit; text-decoration: none; }
.home-lab-poem figcaption a:hover { color: var(--gold); }
@media (max-width: 860px) {
  .home-lab-grid { grid-template-columns: 1fr; }
  .home-lab-frame { max-height: none; }
  .home-lab-stats { grid-template-columns: repeat(2, 1fr); }
}
</style>
